"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { useBookingModal } from "@/contexts/BookingModalContext";
import { routes } from "@/lib/routes";
import { NEW_IMAGES } from "@/lib/images";

export default function PitchesPage() {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();

  const pitches = [
    {
      slug: "parquet-futsal-5",
      titleKey: "chooseCards.card1Title",
      descKey: "chooseCards.card1Desc",
      href: routes.services.parquetFutsal5,
      image: "/new/DSC_2833-2.jpg",
      badgeKey: "chips.parquet",
    },
    {
      slug: "synthetic-futsal-3",
      titleKey: "chooseCards.card2Title",
      descKey: "chooseCards.card2Desc",
      href: routes.services.syntheticFutsal3,
      image: NEW_IMAGES.synthetic[0],
      badgeKey: "chips.synthetic",
    },
  ];

  return (
    <div className="bg-slate-50">
      {/* Hero — layered overlays for readable type (aligned with pricing page) */}
      <section className="relative flex min-h-[300px] flex-col items-center justify-center overflow-hidden px-4 pb-16 pt-10 sm:min-h-[360px] sm:pb-20 md:min-h-[400px]">
        <div className="absolute inset-0 bg-slate-950" aria-hidden />
        <Image
          src="/new/1.jpg"
          alt={t("images.pitchesHero")}
          fill
          priority
          sizes="100vw"
          className="object-cover object-center opacity-[0.42]"
          quality={85}
        />
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_95%_85%_at_50%_15%,transparent_0%,rgba(15,23,42,0.88)_58%,rgb(15,23,42)_100%)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-linear-to-b from-slate-950/25 via-slate-950/65 to-slate-950"
          aria-hidden
        />

        <div className="relative z-10 mx-auto w-full max-w-3xl px-2 text-center">
          <p className="mx-auto mb-5 inline-flex max-w-xl items-center justify-center rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[13px] font-medium leading-snug text-white/95 shadow-sm backdrop-blur-md sm:text-sm">
            {t("chooseCards.subtitle")}
          </p>
          <h1 className="text-balance text-[1.65rem] font-semibold tracking-[-0.02em] text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.45)] sm:text-4xl md:text-5xl">
            {t("header.pitches")} — {t("common.venueName")}
          </h1>
          <nav
            className="mt-5 flex items-center justify-center gap-2 text-[13px] font-medium text-slate-400"
            aria-label="Breadcrumb"
          >
            <Link href={routes.home} className="transition-colors hover:text-white">
              {t("contact.breadcrumbHome")}
            </Link>
            <span className="text-slate-600" aria-hidden>
              —
            </span>
            <span className="text-slate-200">{t("pitches.breadcrumbCanchas")}</span>
          </nav>
          <p className="mx-auto mt-5 max-w-2xl text-pretty text-[15px] leading-relaxed text-white/92 sm:text-lg">
            {t("hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Full-width sections — editorial rows, not card grid */}
      <section className="relative z-10 -mt-10 bg-white pb-20 sm:-mt-12 sm:pb-28">
        <div className="mx-auto w-full max-w-6xl px-4 pt-14 sm:px-6 sm:pt-18">
          <header className="mx-auto max-w-2xl border-b border-slate-200 pb-12 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {t("common.brandName")}
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {t("pitches.sectionTitle")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
              {t("hero.badge")}
            </p>
          </header>

          <div className="divide-y divide-slate-200">
            {pitches.map((pitch, index) => {
              const imageOnLeft = index % 2 === 0;
              return (
                <article
                  key={pitch.slug}
                  className="grid gap-10 py-14 md:grid-cols-2 md:items-center md:gap-14 md:py-20"
                >
                  <div
                    className={
                      imageOnLeft
                        ? "relative min-h-[220px] md:min-h-[320px]"
                        : "relative order-1 min-h-[220px] md:order-2 md:min-h-[320px]"
                    }
                  >
                    <Image
                      src={pitch.image}
                      alt={`${t(pitch.titleKey)} — ${t("common.venueName")}`}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className="object-cover"
                    />
                  </div>

                  <div
                    className={
                      imageOnLeft
                        ? "flex flex-col justify-center"
                        : "order-2 flex flex-col justify-center md:order-1"
                    }
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
                      {t(pitch.badgeKey)}
                    </p>
                    <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-[1.65rem] sm:leading-snug">
                      {t(pitch.titleKey)}
                    </h3>
                    <p className="mt-4 max-w-md text-[15px] leading-relaxed text-slate-600 sm:text-base">
                      {t(pitch.descKey)}
                    </p>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                      <button
                        type="button"
                        onClick={openModal}
                        className="inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary-strong"
                      >
                        {t("chooseCards.card1Cta")}
                      </button>
                      <Link
                        href={pitch.href}
                        className="inline-flex items-center justify-center gap-1.5 py-3 text-sm font-semibold text-primary underline decoration-primary/30 underline-offset-4 transition hover:decoration-primary"
                      >
                        {t("pitches.viewDetails")}
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
